'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { readAnalyticsOptOut } from '@/lib/privacyCookie';

type AnyPostHog = {
  init: (key: string, opts: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  debug?: () => void;
  opt_out_capturing?: () => void;
  opt_in_capturing?: () => void;
};

// Stable context for the (lazily-loaded) PostHog client. CRITICAL: the provider
// element type below never changes — only its `value` flips null→client. That
// is what keeps the page subtree from REMOUNTING when PostHog finishes loading
// on idle. The previous implementation swapped `<>{children}</>` for
// `<Provider client={client}>{children}</Provider>`, and that element-type
// change unmounted+remounted the whole app (Navbar, the page, the hero),
// restarting the hero's CSS entrance animation — the "double flash". A context
// VALUE change only re-renders consumers; it never remounts children.
const PostHogClientContext = createContext<AnyPostHog | null>(null);

/** Reactive accessor for the PostHog client (null until it loads on idle). */
export const usePostHogClient = () => useContext(PostHogClientContext);

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<AnyPostHog | null>(null);

  useEffect(() => {
    // The PostHog project key is a PUBLIC, write-only ingestion key — it ships
    // in the client bundle by design. It comes from the environment only: with
    // no key configured we never initialize the SDK, so a misconfigured deploy
    // simply reports nothing instead of writing into someone else's project.
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!posthogKey) return;

    let cancelled = false;

    const init = async () => {
      // Dynamic import keeps `posthog-js` (~44 KiB) out of the entry bundle —
      // it lands in its own deferred chunk fetched once the main thread is
      // idle. (We no longer import `posthog-js/react`: the client is provided
      // through our own stable context above, so the React tree never changes
      // shape when PostHog loads.)
      const posthogModule = await import('posthog-js');
      if (cancelled) return;

      const posthog = posthogModule.default as AnyPostHog;
      posthog.init(posthogKey, {
        // Prefer a managed reverse proxy (set `NEXT_PUBLIC_POSTHOG_HOST`) so
        // ad blockers — which silently drop 10–25% of direct us.i.posthog.com
        // calls — can't kill landing analytics. Falls back to the direct host
        // when no proxy is configured. The root layout preconnects to the same
        // value.
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false, // We capture pageviews manually in PostHogPageView.
        capture_pageleave: true,
        // Skip loading the PostHog session-recorder (~51 KiB) and surveys
        // (~32 KiB) feature scripts. Re-enable individually if needed.
        disable_session_recording: true,
        disable_surveys: true,
        // Dead-clicks autocapture is off, but the HEATMAPS feature (enabled
        // on the project) lazy-loads the same dead-clicks script — which ad
        // blockers kill, logging "[Dead Clicks] failed to load script" in the
        // console. Keep heatmaps in production (benign, silent for blocked
        // visitors) but skip them in dev so the console stays clean.
        capture_dead_clicks: false,
        ...(process.env.NODE_ENV === 'development'
          ? { enable_heatmaps: false }
          : {}),
        loaded: (loaded: AnyPostHog) => {
          if (process.env.NODE_ENV === 'development' && loaded.debug) {
            loaded.debug();
          }
        },
      });

      // Park the initialized instance on window so non-React listeners
      // (the document click delegate, IntersectionObserver views) can read
      // the same singleton. The privacy dialog already grabs it this way.
      (window as unknown as { posthog?: typeof posthog }).posthog = posthog;

      // Respect the user's Analytics opt-out cookie. The dialog writes
      // `as_analytics_optout=true` when the user turns Analytics off
      // (and middleware writes it on GPC). PostHog persists this opt-out
      // in its own localStorage entry, so the check only needs to fire
      // once per page load — subsequent events stay suppressed.
      if (readAnalyticsOptOut() && posthog.opt_out_capturing) {
        posthog.opt_out_capturing();
      }

      setClient(posthog);
    };

    // Defer load+init until the browser is idle so analytics doesn't
    // compete with hero paint / hydration. Fallback: short timeout for
    // browsers without requestIdleCallback.
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    }).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    // /launch fires the `autoscreenshot://` scheme during parse. The resulting
    // pending external navigation stops the page going idle, so
    // requestIdleCallback never fires — not even on its 2s timeout — and
    // PostHog was never initialising there at all (measured: `window.posthog`
    // still undefined 19s in). That silently zeroes the /launch pageview.
    //
    // Initialising eagerly here removes the idle dependency, which is safe on
    // this page: no hero to compete with, and it decides within 2.5s. Note it
    // is NOT a complete fix — in a browser with no handler registered for the
    // scheme, the pending navigation also blocks chunk loading, so hydration
    // never completes and this effect never runs. Anything measured FROM
    // /launch is therefore best-effort; the reliable signal is
    // `launch_fell_back`, which fires on /download after the redirect.
    const eager = window.location.pathname === "/launch";
    if (eager) {
      void init();
      return () => { cancelled = true; };
    }

    const handle = ric
      ? ric(() => { void init(); }, { timeout: 2000 })
      : window.setTimeout(() => { void init(); }, 1500);

    return () => {
      cancelled = true;
      const cancelRic = (window as Window & {
        cancelIdleCallback?: (handle: number) => void;
      }).cancelIdleCallback;
      if (ric && typeof cancelRic === "function") {
        cancelRic(handle);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);

  // ALWAYS the same element type — only `value` changes (null → client) when
  // PostHog loads. Children are never remounted, so the hero entrance plays
  // exactly once. Consumers (PostHogPageView via usePostHogClient) re-render
  // reactively when the client becomes available.
  return (
    <PostHogClientContext.Provider value={client}>
      {children}
    </PostHogClientContext.Provider>
  );
}
